import { ArrowRight, MessageSquare, Sparkles } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ChatAgente } from "@/components/agenti/ChatAgente";
import { AgenteSlot } from "@/components/dashboard/AgenteSlot";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useModuloSommario } from "@/hooks/useDashboard";
import { cn } from "@/lib/utils";
import type { MetricaModulo, ModuloSommario } from "@/types/api";

/** Rotta "Apri modulo" per i moduli che ne hanno una. */
const MODULO_ROUTE: Record<string, string> = {
  M1: "/moduli/order-readiness",
  M2: "/moduli/pianificazione",
  M3: "/moduli/spedizioni",
};

const STATO_BADGE: Record<string, string> = {
  roadmap: "bg-muted text-muted-foreground",
  anteprima: "bg-amber-100 text-amber-700",
  beta: "bg-brand-100 text-brand-700",
  live: "bg-emerald-100 text-emerald-700",
};

const TONO_METRICA: Record<string, string> = {
  warning: "text-amber-600",
  danger: "text-red-600",
  success: "text-emerald-600",
};

export interface ModuloCardConfig {
  modulo_codice?: string;
  variante?: "full" | "compact";
  agente_in_arrivo?: boolean;
}

interface Props {
  titolo: string;
  descrizione?: string | null;
  config: ModuloCardConfig | null;
}

function tempoRelativo(iso: string | null): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ora";
  if (min < 60) return `${min}m fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h fa`;
  return `${Math.floor(h / 24)}g fa`;
}

export function ModuloCard({ titolo, descrizione, config }: Props) {
  const codice = config?.modulo_codice;
  const variante = config?.variante ?? "full";
  const isPseudo = !!config?.agente_in_arrivo || !codice;

  // I pseudo-moduli (es. assistente di presidenza) non hanno sommario backend.
  const sommarioQuery = useModuloSommario(codice, !isPseudo);
  const sommario = sommarioQuery.data;

  return (
    <ModuloCardShell
      titolo={titolo}
      descrizione={descrizione}
      codice={codice}
      sommario={sommario}
      loading={!isPseudo && sommarioQuery.isLoading}
      isPseudo={isPseudo}
      variante={variante}
    />
  );
}

function IconaModulo({ codice }: { codice?: string }) {
  return (
    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-700 text-xs font-bold text-white">
      {codice && /^M\d$/.test(codice) ? (
        codice
      ) : (
        <Sparkles className="size-4" aria-hidden="true" />
      )}
    </div>
  );
}

function StatoBadge({ stato }: { stato?: string }) {
  if (!stato) return null;
  return (
    <span
      className={cn(
        "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        STATO_BADGE[stato] ?? "bg-muted text-muted-foreground"
      )}
    >
      {stato}
    </span>
  );
}

function ModuloCardShell({
  titolo,
  descrizione,
  codice,
  sommario,
  loading,
  isPseudo,
  variante,
}: {
  titolo: string;
  descrizione?: string | null;
  codice?: string;
  sommario?: ModuloSommario;
  loading: boolean;
  isPseudo: boolean;
  variante: "full" | "compact";
}) {
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const agente = sommario?.agente ?? null;
  const route = codice ? MODULO_ROUTE[codice] : undefined;

  if (variante === "compact") {
    return (
      <div className="flex items-center gap-3 rounded-xl border bg-card/60 p-4">
        <IconaModulo codice={codice} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold">{titolo}</span>
            <StatoBadge stato={sommario?.stato} />
          </div>
          <p className="text-xs text-muted-foreground">
            {agente?.attivo
              ? `${agente.nome} · attivo`
              : "Agente in arrivo"}
          </p>
        </div>
        {route && (
          <Button variant="ghost" size="sm" onClick={() => navigate(route)}>
            <ArrowRight className="size-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <section className="space-y-3 rounded-xl border bg-card/60 p-5">
      <header className="flex items-start gap-3">
        <IconaModulo codice={codice} />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-sm font-semibold tracking-tight">{titolo}</h2>
            <StatoBadge stato={sommario?.stato} />
          </div>
          {descrizione && (
            <p className="text-xs text-muted-foreground">{descrizione}</p>
          )}
        </div>
      </header>

      {loading ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <>
          <AgenteSlot agente={isPseudo ? null : agente} />

          {sommario && sommario.metriche.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {sommario.metriche.map((m: MetricaModulo) => (
                <div key={m.label} className="rounded-lg border bg-background/60 p-2">
                  <p
                    className={cn(
                      "text-lg font-semibold tabular-nums",
                      m.tono ? TONO_METRICA[m.tono] : "text-foreground"
                    )}
                  >
                    {m.valore}
                  </p>
                  <p className="text-[11px] leading-tight text-muted-foreground">
                    {m.label}
                  </p>
                </div>
              ))}
            </div>
          )}

          {agente?.attivo && agente.ultima_attivita && (
            <p className="text-xs text-muted-foreground">
              {agente.ultima_attivita.testo}
              {agente.ultima_attivita.quando
                ? ` · ${tempoRelativo(agente.ultima_attivita.quando)}`
                : ""}
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            {route && (
              <Button size="sm" onClick={() => navigate(route)}>
                Apri modulo
              </Button>
            )}
            {agente?.attivo && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setChatOpen(true)}
              >
                <MessageSquare className="size-4" /> Parla con {agente.nome}
              </Button>
            )}
          </div>
        </>
      )}

      <ChatAgente
        aperta={chatOpen}
        onApertaChange={setChatOpen}
        nome={agente?.nome ?? "Assistente"}
        stato={agente?.descrizione ?? titolo}
        basePath="/api/v1/m3/agente/chat"
        modo="overlay"
        mostraBarra={false}
      />
    </section>
  );
}
